'use strict';

/**
 * T451 P3.1 — the pure task-state helpers, shared by the SERVER (index.js
 * delegates to these) and the BROWSER (shared/cores/tasks/task-store-unseal.js,
 * delivered through the vault bundle).
 *
 * Moved VERBATIM out of index.js (sanitizeTaskOwnerKey :7690, ensureUserTaskState
 * :6582, cloneTaskState :6637, stripAutoSourceLists :7367, normalizeTaskStateForUser
 * :7356 at 324709165). index.js keeps one-line delegates under the same names, so
 * every existing call site and export is unchanged and there is ONE normalizer,
 * not a server copy and a browser twin that drift apart.
 *
 * BROWSER-SAFE: no node: imports, no fs, no env. Pure functions over plain objects.
 */

function sanitizeTaskOwnerKey(value) {
  if (value == null) return null;
  const trimmed = String(value).trim().replace(/[^:a-zA-Z0-9_-]/g, '-');
  if (!trimmed || trimmed === '__proto__' || trimmed === 'constructor' || trimmed === 'prototype') return null;
  return trimmed || null;
}

function ensureUserTaskState(store, ownerKey, fallbackKeys = []) {
  if (!store || typeof store !== 'object') {
    throw new Error('Task store is required.');
  }
  if (!store.users || typeof store.users !== 'object') {
    store.users = {};
  }
  const normalizedKey = sanitizeTaskOwnerKey(ownerKey);
  if (!normalizedKey) {
    throw new Error('Valid task owner key required.');
  }
  const normalizedFallbacks = Array.isArray(fallbackKeys)
    ? fallbackKeys
        .map((entry) => sanitizeTaskOwnerKey(entry))
        .filter((entry) => entry && entry !== normalizedKey)
    : [];
  let state = store.users[normalizedKey];
  if (!state) {
    for (const candidate of normalizedFallbacks) {
      const existing = store.users[candidate];
      if (existing && typeof existing === 'object') {
        state = existing;
        delete store.users[candidate];
        break;
      }
    }
  }
  const now = new Date().toISOString();
  if (!state || typeof state !== 'object') {
    state = {
      lists: {},
      tasks: {},
      rootListIds: [],
      createdAt: now,
      updatedAt: now,
      lastUsedAt: now,
    };
    store.users[normalizedKey] = state;
    return state;
  }
  if (!state.lists || typeof state.lists !== 'object') {
    state.lists = {};
  }
  if (!state.tasks || typeof state.tasks !== 'object') {
    state.tasks = {};
  }
  if (!Array.isArray(state.rootListIds)) {
    state.rootListIds = [];
  }
  if (!store.users[normalizedKey]) {
    store.users[normalizedKey] = state;
  }
  return state;
}

function cloneTaskState(userState) {
  const lists = userState?.lists && typeof userState.lists === 'object' ? userState.lists : {};
  const tasks = userState?.tasks && typeof userState.tasks === 'object' ? userState.tasks : {};
  const clone = {
    lists: JSON.parse(JSON.stringify(lists)),
    tasks: JSON.parse(JSON.stringify(tasks)),
    rootListIds: Array.isArray(userState?.rootListIds) ? [...userState.rootListIds] : [],
    createdAt: userState?.createdAt || null,
    updatedAt: userState?.updatedAt || null,
    lastUsedAt: userState?.lastUsedAt || null,
  };
  return clone;
}

// T469 LD-13 — source separation. The per-user /api/task-lists response must
// NEVER carry an auto-source list (e.g. source.generator==='master-todo').
// Auto-lists are served ONLY by the operator-gated /api/master-todo/code
// endpoint; the Code▸Task List container composes the two sources client-side.
// This is a defensive strip: the create path never stamps `auto`, but a
// poisoned/legacy store entry must not leak across the per-user boundary.
function stripAutoSourceLists(state) {
  if (!state || !state.lists || typeof state.lists !== 'object') return state;
  const autoIds = [];
  for (const [id, list] of Object.entries(state.lists)) {
    if (list && list.source && list.source.kind === 'auto') autoIds.push(id);
  }
  if (autoIds.length === 0) return state;
  const autoSet = new Set(autoIds);
  for (const id of autoIds) delete state.lists[id];
  if (Array.isArray(state.rootListIds)) {
    state.rootListIds = state.rootListIds.filter((id) => !autoSet.has(id));
  }
  for (const list of Object.values(state.lists)) {
    if (list && Array.isArray(list.childListIds)) {
      list.childListIds = list.childListIds.filter((id) => !autoSet.has(id));
    }
  }
  return state;
}

function normalizeTaskStateForUser(store, ownerKey, options = {}) {
  const fallbackKeys = Array.isArray(options?.fallbackKeys) ? options.fallbackKeys : [];
  const userState = ensureUserTaskState(store, ownerKey, fallbackKeys);
  return stripAutoSourceLists(cloneTaskState(userState));
}

// T881 P3 (moved VERBATIM from index.js :6212 at 47f311a82 by T451 P3.4): with
// an owner key, an account's file carries ONLY that account's blocks —
// narrowing at the SINK, so a caller cannot widen the blast radius of an
// account's envelope by handing over a multi-tenant store. Browser-shared so
// the sealed WRITE narrows exactly as the server sink does.
function narrowStoreToOwner(store, ownerKey, fallbackKeys = []) {
  const users = store?.users && typeof store.users === 'object' ? store.users : {};
  const keep = [ownerKey, ...(Array.isArray(fallbackKeys) ? fallbackKeys : [])]
    .map((entry) => sanitizeTaskOwnerKey(entry))
    .filter(Boolean);
  const narrowed = {};
  for (const key of keep) {
    if (Object.prototype.hasOwnProperty.call(users, key) && users[key]) {
      narrowed[key] = users[key];
    }
  }
  return narrowed;
}

// T451 P3.4 — the at-rest payload SHAPE the server sink writes
// (index.js writeUserTaskStore: `{ version:1, users:<narrowed>, updatedAt:<now> }`),
// built browser-side for the sealed write so the two never drift.
function buildTaskStorePayload(store, ownerKey, fallbackKeys = []) {
  const key = ownerKey ? sanitizeTaskOwnerKey(ownerKey) : null;
  return {
    version: 1,
    users: key
      ? narrowStoreToOwner(store, key, fallbackKeys)
      : (store?.users && typeof store.users === 'object' ? store.users : {}),
    updatedAt: new Date().toISOString(),
  };
}

module.exports = {
  sanitizeTaskOwnerKey,
  ensureUserTaskState,
  cloneTaskState,
  stripAutoSourceLists,
  normalizeTaskStateForUser,
  narrowStoreToOwner,
  buildTaskStorePayload,
};
